import React from 'react';

import Input from '@plasma/ui.input.input';
import Select from '@plasma/ui.input.select';
import Switch from '@plasma/ui.input.switch';

import * as styled from './all-personnel.styles';

const { Option } = Select;

export interface PersonnelFormValues {
  id?: string;
  companyId?: string;
  isActive: boolean;
  personnelName: string;
  personnelEmail: string;
  designation: string;
  department: string;
  flagInternal?: number;
  address1: string;
  address2: string;
  address3: string;
  province: string;
  state: string;
  continent: string;
  country: string;
  zipCode: string;
  contactNo1: string;
  contactNo2: string;
  entityCode: string;
}

export interface PersonnelFormProps {
  values: PersonnelFormValues;
  onChange: <K extends keyof PersonnelFormValues>(field: K, value: PersonnelFormValues[K]) => void;
  onZipCodeChange: (value: string) => void;
  onContactNo1Change: (value: string) => void;
  onContactNo2Change: (value: string) => void;
  emailError?: string;
  flagOptions: Record<string, string>;
  entityOption: string[];
  t: (key: string) => string;
}

const PersonnelForm: React.FC<PersonnelFormProps> = ({
  values,
  onChange,
  onZipCodeChange,
  onContactNo1Change,
  onContactNo2Change,
  emailError,
  entityOption,
  flagOptions,
  t,
}) => {
  return (
    <>
      <styled.ModalTopBar>
        <styled.StatusToggleBox $isActive={values.isActive}>
          <div> {t('active')} : </div>
          <Switch size="small" checked={values.isActive} onChange={(checked) => onChange('isActive', checked)} />
        </styled.StatusToggleBox>
      </styled.ModalTopBar>

      <styled.ModalContainer>
        <styled.ModalHeaderContainer>{t('personnel')}</styled.ModalHeaderContainer>
        <styled.FormGrid $columns={3}>
          <styled.FormGroup>
            <styled.Label htmlFor="personnelName">{t('name')} :</styled.Label>
            <Input
              id="personnelName"
              value={values.personnelName}
              type="text"
              onChange={(e) => onChange('personnelName', e.target.value)}
            />
          </styled.FormGroup>
          <styled.FormGroup>
            <styled.Label htmlFor="personnelEmail">{t('email_id')} :</styled.Label>
            <Input
              id="personnelEmail"
              value={values.personnelEmail}
              type="email"
              onChange={(e) => onChange('personnelEmail', e.target.value)}
            />
            {emailError && <styled.ErrorText>{emailError}</styled.ErrorText>}
          </styled.FormGroup>
          <styled.FormGroup>
            <styled.Label>{t('sms_customer')} :</styled.Label>
            <Select
              value={
                values.flagInternal !== undefined && values.flagInternal !== null
                  ? String(values.flagInternal)
                  : undefined
              }
              onChange={(value) => onChange('flagInternal', Number(value))}
            >
              {Object.keys(flagOptions).map((key) => (
                <Option key={key} value={key}>
                  {flagOptions[key]}
                </Option>
              ))}
            </Select>
          </styled.FormGroup>
        </styled.FormGrid>

        <styled.ModalHeaderContainer>{t('work_information')}</styled.ModalHeaderContainer>
        <styled.FormGrid $columns={3}>
          <styled.FormGroup>
            <styled.Label>{t('designation')} :</styled.Label>
            <Input value={values.designation} type="text" onChange={(e) => onChange('designation', e.target.value)} />
          </styled.FormGroup>
          <styled.FormGroup>
            <styled.Label>{t('department')} :</styled.Label>
            <Input value={values.department} type="text" onChange={(e) => onChange('department', e.target.value)} />
          </styled.FormGroup>
          <styled.FormGroup>
            <styled.Label>{t('entity_code')} :</styled.Label>
            <Select
              value={values.entityCode ? String(values.entityCode) : undefined}
              onChange={(value) => onChange('entityCode', value)}
            >
              {entityOption?.map((entity) => (
                <Option key={entity} value={entity}>
                  {entity}
                </Option>
              ))}
            </Select>
          </styled.FormGroup>
        </styled.FormGrid>

        <styled.ModalHeaderContainer>{t('address_detail')}</styled.ModalHeaderContainer>
        <styled.FormGrid $columns={4}>
          <styled.FormGroup>
            <styled.Label>{t('address_1')} :</styled.Label>
            <Input value={values.address1} type="text" onChange={(e) => onChange('address1', e.target.value)} />
          </styled.FormGroup>
          <styled.FormGroup>
            <styled.Label>{t('address_2')} :</styled.Label>
            <Input value={values.address2} type="text" onChange={(e) => onChange('address2', e.target.value)} />
          </styled.FormGroup>
          <styled.FormGroup>
            <styled.Label>{t('address_3')} :</styled.Label>
            <Input value={values.address3} onChange={(e) => onChange('address3', e.target.value)} />
          </styled.FormGroup>
          <styled.FormGroup>
            <styled.Label>{t('province')} :</styled.Label>
            <Input value={values.province} onChange={(e) => onChange('province', e.target.value)} />
          </styled.FormGroup>
          <styled.FormGroup>
            <styled.Label>{t('state')} :</styled.Label>
            <Input value={values.state} onChange={(e) => onChange('state', e.target.value)} />
          </styled.FormGroup>
          <styled.FormGroup>
            <styled.Label>{t('continent')} :</styled.Label>
            <Input value={values.continent} onChange={(e) => onChange('continent', e.target.value)} />
          </styled.FormGroup>
          <styled.FormGroup>
            <styled.Label>{t('country')} :</styled.Label>
            <Input value={values.country} onChange={(e) => onChange('country', e.target.value)} />
          </styled.FormGroup>
          <styled.FormGroup>
            <styled.Label>{t('zip_code')} :</styled.Label>
            <Input value={values.zipCode} onChange={(e) => onZipCodeChange(e.target.value)} />
          </styled.FormGroup>
        </styled.FormGrid>

        <styled.ModalHeaderContainer>{t('contact_detail')}</styled.ModalHeaderContainer>
        <styled.FormGrid $columns={2}>
          <styled.FormGroup>
            <styled.Label>{t('contact_no_1')} :</styled.Label>
            <Input value={values.contactNo1} type="tel" onChange={(e) => onContactNo1Change(e.target.value)} />
          </styled.FormGroup>
          <styled.FormGroup>
            <styled.Label>{t('contact_no_2')} :</styled.Label>
            <Input value={values.contactNo2} type="tel" onChange={(e) => onContactNo2Change(e.target.value)} />
          </styled.FormGroup>
        </styled.FormGrid>
      </styled.ModalContainer>
    </>
  );
};

export default PersonnelForm;
